<template>
  <div class="d-flex flex-column flex-grow-1 min-h-0">
    <!-- Header -->
    <div class="header"><h3>Your custom area</h3></div>
    <v-divider></v-divider>
    <!-- Component Body: fills rest -->
    <div
      class="component-body d-flex flex-column flex-grow-1 min-h-0 overflow-hidden"
    >
      <div class="split" :class="showBottomPane ? 'split--two' : 'split--one'">
        <!-- Top pane -->
        <div class="pane-top">
          <h4 class="px-4 pt-4 pb-2 font-weight-medium">Selected areas</h4>

          <!-- Empty state -->
          <div
            v-if="showEmptyState"
            class="flex-grow-1 d-flex align-center justify-center min-h-0"
          >
            <AvatarState
              v-show="screenHeight > 730"
              avatarSize="102"
              icon="mdi-checkbox-marked"
              iconSize="54"
            >
              <template #title>Your Selections</template>
              <template #body>Your selections will appear here</template>
            </AvatarState>
          </div>

          <!-- List -->
          <div v-else class="d-flex flex-column flex-grow-1 min-h-0">
            <div
              class="pane-top__scroll d-flex flex-column flex-grow-1 min-h-0"
            >
              <v-list
                role="group"
                aria-label="List of selected areas"
                class="py-0"
              >
                <v-list-item
                  v-for="area in selectedAreaData"
                  :key="area.id"
                  class="text-left justify-space-between w-100"
                  :class="{
                    'error-item': selectedAreaErrorIds.has(String(area.id)),
                  }"
                >
                  <template #title>{{ selectedAreaName(area.id) }} </template>

                  <template #append>
                    <!-- pill for error -->
                    <v-chip
                      v-if="selectedAreaErrorIds.has(String(area.id))"
                      size="small"
                      color="error"
                      class="ml-2"
                      label
                    >
                      Save failed
                      <v-tooltip location="top" open-on-click>
                        <template #activator="{ props }">
                          <v-btn
                            icon
                            size="x-small"
                            rounded="0"
                            variant="text"
                            class="ml-1"
                            aria-label="Error details"
                            v-bind="props"
                          >
                            <v-icon>mdi-information-outline</v-icon>
                          </v-btn>
                        </template>

                        <div style="white-space: pre-line">
                          {{ errorReasonForId(area.id) || "Unknown error" }}
                        </div>
                      </v-tooltip>
                    </v-chip>

                    <!-- edit single area name (multiple custom areas)-->
                    <v-btn
                      v-if="!singleCustomArea"
                      icon
                      variant="plain"
                      size="small"
                      rounded="0"
                      @click="
                        editNameAreaId = area.id;
                        showEditNameDialog = true;
                      "
                    >
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>

                    <!-- delete single area button -->
                    <v-btn
                      icon
                      variant="plain"
                      size="small"
                      color="error"
                      rounded="0"
                      @click="removeSelectedAreaIds(area.id)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                </v-list-item>
              </v-list>
            </div>

            <div
              class="pane-top__footer d-flex justify-center mt-auto px-4 py-2"
            >
              <v-btn
                variant="plain"
                size="default"
                color="error"
                rounded="0"
                @click="clearSelectedAreaIds()"
              >
                Remove All Areas
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Bottom pane (Optional) -->
        <div v-if="showBottomPane" class="pane-bottom border-t-thin pa-4">
          <div
            class="d-flex flex-column flex-grow-1 min-h-0 overflow-y-auto"
            style="overflow-x: visible"
          >
            <h4 class="font-weight-medium pb-2">Area Details</h4>

            <!-- single custom area switch -->
            <v-switch
              v-if="showSingleCustomAreaSwitch"
              v-model="singleCustomAreaField"
              color="primary"
              class="ps-3 mb-8 w-100 d-flex flex-column flex-wrap align-start label-details-tight"
              density="compact"
            >
              <template #label>
                <div class="text-body-2 font-weight-medium">
                  Set-up as single Custom Area
                </div>
              </template>
              <template #details>
                <div class="ml-9 pt-2">
                  <span v-if="singleCustomArea"
                    >All selected areas will be combined to create a single
                    Custom Area</span
                  >
                  <span v-else
                    >All selected areas will be set-up as individual Custom
                    Areas</span
                  >
                </div>
              </template>
            </v-switch>

            <!-- custom area name and category form -->
            <div class="d-flex flex-column align-start">
              <v-text-field
                v-if="singleCustomArea"
                v-model="customAreaNameField"
                :error="formErrors['name']?.length > 0"
                :error-messages="formErrors['name']"
                label="Area name"
                placeholder="Custom area name"
                variant="outlined"
                density="compact"
                class="w-100"
                rounded="0"
                autocomplete="off"
              />
              <v-select
                ref="categorySelect"
                v-model="categorySelectProxy"
                label="Category"
                placeholder="Select category"
                :items="customAreaCategories"
                item-title="name"
                item-value="id"
                return-object
                clearable
                variant="outlined"
                density="compact"
                class="w-100"
                rounded="0"
                :loading="fetchingCategories"
              >
                <!-- Show 'Uncategorised' text in the field when v-model is null -->
                <template #selection>
                  <span v-if="selectedCategoryId === null">Uncategorised</span>
                  <span v-else>{{
                    customAreaCategoryById(selectedCategoryId)?.name
                  }}</span>
                </template>

                <template #prepend-item>
                  <v-list-item
                    base-color="primary"
                    title="Create category"
                    prepend-icon="mdi-plus"
                    link
                    ripple
                    @click="
                      showCreateCategoryDialog = true;
                      closeCategorySelect();
                    "
                  />
                  <v-divider />
                  <v-list-item
                    title="Uncategorised"
                    :value="null"
                    link
                    ripple
                    density="compact"
                    @click="
                      setSelectedCategoryId(null);
                      closeCategorySelect();
                    "
                    :active="selectedCategoryId === null"
                    prepend-icon="mdi-tag-off-outline"
                  />
                </template>
              </v-select>
              <v-textarea
                v-if="singleCustomArea"
                v-model="customAreaDescriptionField"
                :error="formErrors['description']?.length > 0"
                :error-messages="formErrors['description']"
                label="Description"
                placeholder="Description"
                rows="3"
                class="w-100"
                rounded="0"
                variant="outlined"
                density="compact"
                autocomplete="off"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Category Dialog -->
    <DynamicDialog
      v-model:show="showCreateCategoryDialog"
      @dialogOk="createNewCategory"
      @dialogCancel="resetNewCategoryDialog"
      :okBtnDisabled="newCategoryName.trim().length === 0 || creatingCategory"
      okBtnColor="success"
      okBtnVariant="elevated"
      cancelBtnColor="none"
      cancelBtnVariant="text"
      max-width="40vw"
    >
      <template v-slot:title>Create Category</template>
      <template v-slot:content>
        Enter a name for this category
        <v-text-field
          v-model="newCategoryName"
          :error="newCategoryNameError.length > 0"
          :error-messages="newCategoryNameError"
          :loading="creatingCategory"
          label="Category name"
          variant="outlined"
          density="compact"
          class="w-100 mt-4"
        />
      </template>
      <template v-slot:okBtnTitle>Create Category</template>
    </DynamicDialog>

    <!-- Edit area name dialog -->
    <DynamicDialog
      v-model:show="showEditNameDialog"
      @dialogOk="updateNameOverride"
      @dialogCancel="resetNameEditDialog"
      okBtnColor="success"
      okBtnVariant="elevated"
      cancelBtnColor="none"
      cancelBtnVariant="text"
      max-width="40vw"
    >
      <template v-slot:title>Edit Custom Area Name</template>
      <template v-slot:content>
        Edit the name for this custom area
        <v-text-field
          v-model="editNameValue"
          :error="editNameError.length > 0"
          :error-messages="editNameError"
          label="Custom Area Name"
          variant="outlined"
          density="compact"
          class="w-100 mt-4"
          append-inner-icon="mdi-restore"
          @click:append-inner="restoreAreaName(editNameAreaId)"
        ></v-text-field>
      </template>
      <template v-slot:okBtnTitle>Update Name</template>
    </DynamicDialog>
  </div>
</template>

<script>
import { systemMessages } from "@/mixins/SystemMessages";
import { useDisplay } from "vuetify";

import AvatarState from "@/components/AvatarState.vue";
import DynamicDialog from "@/components/DynamicDialog";

import { createNamespacedHelpers } from "vuex";
const { mapState, mapMutations, mapGetters } =
  createNamespacedHelpers("customAreaManager");

// const PREVENT_MULTIPLE_CUSTOM_AREAS = [0, 1, 2]; //area level ids e.g 0 = OA, 1 = LSOA, 2 = MSOA

export default {
  name: "AreaListStandard",
  components: { AvatarState, DynamicDialog },
  mixins: [systemMessages],
  props: {},
  mounted() {
    this.fetchCustomAreaCategories();
  },
  computed: {
    //states from VUEX to read values e.g. this.standardAreas
    ...mapState([
      "selectedAreaIds",
      "customAreaCategories",
      "selectedCategoryId",
      "customAreaName",
      "customAreaNameOverrides",
      "customAreaDescription",
      "singleCustomArea",
      "formErrors",
      "errorAreaIds",
      "errorAreaReasons",
      "selectedAreaDataLevelId",
    ]),
    //getters from VUEX to read derived values
    ...mapGetters([
      "areaDataForLevelTimepoint",
      "customAreaCategoryById",
      "selectedAreaData",
      "errorReasonForId",
    ]),
    // boolean - there are no selected areas so show empty state
    showEmptyState() {
      return this.selectedAreaIds.length === 0;
    },
    // Proxy for category select to handle 'Uncategorised' label display in select list
    categorySelectProxy: {
      get() {
        return this.selectedCategoryId === null
          ? { id: null, name: "Uncategorised" } // display only
          : this.customAreaCategoryById(this.selectedCategoryId);
      },
      set(val) {
        // clear button or manual clearing
        if (!val) {
          this.setSelectedCategoryId(null);
          return;
        }

        // map select option back to null for 'Uncategorised'
        if (val.id === null) this.setSelectedCategoryId(null);
        else this.setSelectedCategoryId(val.id);
      },
    },
    //manage vuex single custom area switch sync
    singleCustomAreaField: {
      get() {
        return this.singleCustomArea;
      },
      set(val) {
        this.setSingleCustomArea(val);
      },
    },
    //manage vuex area name sync
    customAreaNameField: {
      get() {
        return this.customAreaName;
      },
      set(val) {
        this.setCustomAreaName(val);
      },
    },
    //manager vuex area description sync
    customAreaDescriptionField: {
      get() {
        return this.customAreaDescription;
      },
      set(val) {
        this.setCustomAreaDescription(val);
      },
    },
    //get/set value for multiple custom areas name edit
    editNameValue: {
      get() {
        if (this.editNameNewVal === null) {
          return (
            this.customAreaNameOverrides[this.editNameAreaId] ||
            this.selectedAreaData.find((a) => a.id === this.editNameAreaId)
              ?.area_name
          );
        }
        return this.editNameNewVal;
      },
      set(val) {
        this.editNameNewVal = val;
      },
    },
    //set of selected area ids with errors
    selectedAreaErrorIds() {
      return new Set(this.errorAreaIds.map(String));
    },
    //whether to show single/multiple custom area switch
    showSingleCustomAreaSwitch() {
      if (!this.selectedAreaData?.length) {
        return false;
      }

      return true;
    },
  },
  data() {
    return {
      screenHeight: useDisplay().height,
      showBottomPane: true, //future use
      fetchingCategories: false,
      creatingCategory: false,
      newCategoryName: "",
      newCategoryNameError: "",
      showCreateCategoryDialog: false,
      showEditNameDialog: false,
      editNameAreaId: null,
      editNameNewVal: null,
      editNameError: "",
    };
  },
  watch: {
    /** ensure single custom area toggle is set when area level changes (to ensure PREVENT_MULTIPLE_CUSTOM_AREAS is honoured) */
    selectedAreaDataLevelId() {
      this.setSingleCustomArea(true);
    },
  },
  methods: {
    //mutations from VUEX to set state values e.g. this.setStandardAreas(value)
    ...mapMutations([
      "removeSelectedAreaIds",
      "clearSelectedAreaIds",
      "setCustomAreaCategories",
      "setSelectedCategoryId",
      "setCustomAreaName",
      "setCustomAreaDescription",
      "setSingleCustomArea",
      "addCustomAreaNameOverride",
      "removeCustomAreaNameOverride",
      "setFormErrors",
    ]),
    /** custom area categories fetch */
    async fetchCustomAreaCategories() {
      try {
        this.fetchingCategories = true;
        const response = await this.$axios.get("/custom-area-categories");
        this.setCustomAreaCategories(response.data);
      } catch (e) {
        const eDetail = this.errorExtraction(e);
        this.errorMsg({
          message: eDetail.message,
          title: "Error! Failed to get custom area categories",
        });
      } finally {
        this.fetchingCategories = false;
      }
    },
    /** close category select menu */
    closeCategorySelect() {
      const sel = this.$refs.categorySelect;
      sel?.blur?.();
      if ("menu" in sel) sel.menu = false;
      if ("isMenuActive" in sel) sel.isMenuActive = false;
    },
    /** create new custom area category */
    async createNewCategory() {
      //validate input (length 0 or > 255)
      const name = this.newCategoryName.trim();
      if (name.length === 0) {
        this.newCategoryNameError = "Category name cannot be empty";
        return;
      } else if (name.length > 255) {
        this.newCategoryNameError =
          "Category name cannot be longer than 255 characters";
        return;
      } else {
        this.newCategoryNameError = "";
      }

      try {
        this.creatingCategory = true;
        const response = await this.$axios.post("/custom-areas/category", {
          name: name,
        });
        //category created - update list and select new category
        await this.fetchCustomAreaCategories();
        this.$emit("refreshCategories");
        this.categorySelectProxy = response.data;
        this.resetNewCategoryDialog();
      } catch (e) {
        const eDetail = this.errorExtraction(e);
        //show validation error in dialog
        if (eDetail.status === 422) {
          const message = eDetail.error?.name?.[0] || eDetail.message;
          this.newCategoryNameError = message;
          return;
        }
        //throw generic error message
        this.errorMsg({
          message: eDetail.message,
          title: "Error! Failed to create new category",
        });
        this.resetNewCategoryDialog();
      } finally {
        this.creatingCategory = false;
      }
    },
    /** reset new category dialog */
    resetNewCategoryDialog() {
      this.newCategoryName = "";
      this.newCategoryNameError = "";
      this.showCreateCategoryDialog = false;
    },
    // selected area name taking into account any override for multiple custom areas
    selectedAreaName(areaId) {
      return (
        this.customAreaNameOverrides[areaId] ||
        this.selectedAreaData.find((a) => a.id === areaId)?.area_name
      );
    },
    /** set new custom area name override for multi custom areas */
    updateNameOverride() {
      //validate name - greater than 0 length <= 255
      const name = this.editNameNewVal?.trim() || "";
      if (name.length === 0) {
        this.editNameError = "Custom area name cannot be empty";
        return;
      }

      if (name.length > 255) {
        this.editNameError =
          "Custom area name cannot be longer than 255 characters";
        return;
      }
      //if the name matches the original area name then remove any override
      const originalName = this.selectedAreaData.find(
        (a) => a.id === this.editNameAreaId,
      )?.area_name;
      if (name === originalName) {
        this.removeCustomAreaNameOverride(this.editNameAreaId);
      } else {
        //otherwise add/update override
        this.addCustomAreaNameOverride({
          areaId: this.editNameAreaId,
          name: this.editNameNewVal.trim(),
        });
      }

      this.resetNameEditDialog();
    },
    /** reset multi custom area name edit dialog */
    resetNameEditDialog() {
      this.editNameAreaId = null;
      this.editNameNewVal = null;
      this.editNameError = "";
      this.showEditNameDialog = false;
    },
    /** restore original area name in name edit dialog (multi custom area) */
    restoreAreaName(areaId) {
      this.editNameNewVal = this.selectedAreaData.find(
        (a) => a.id === areaId,
      ).area_name;
    },
    /**
     * helper function to handle error messages/types
     */
    errorExtraction(e) {
      const status = e?.response?.status;
      const serverData = e?.response?.data;
      const serverMsg =
        typeof serverData === "string"
          ? serverData
          : serverData?.message || serverData?.error || serverData?.detail;
      const canceled = e?.code === "ERR_CANCELED" || e?.message === "canceled";

      const msg = canceled ? "Request was canceled." : serverMsg || e?.message;

      const error =
        e?.response?.data?.error || e?.response?.data?.errors || null;

      return { status: status, message: msg, error: error };
    },
  },
};
</script>

<style scoped>
.min-h-0 {
  min-height: 0;
}

.header {
  flex: 0 0 auto; /* <- do NOT shrink */
  display: flex; /* nicer vertical centering */
  align-items: center;
  height: 54px;
  padding: 4px 16px;
  background-color: #f9f9f9;

  h3 {
    font-weight: 600;
    color: #4d4d4d;
  }
}

.pane-top,
.pane-bottom {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0; /* important for flex children to scroll instead of expand */
}

/* Grid split enforces the ratio exactly */
.split {
  display: grid;
  height: 100%;
  min-height: 0;
  gap: 0; /* no gap between panes */
}

/** 60/40 when bottom visible */
.split--two {
  grid-template-rows: 1fr auto;
}
.split--one {
  grid-template-rows: 1fr;
}

.label-details-tight :deep(.v-input__details) {
  padding-top: 0 !important;
  margin-top: -16px !important;
} /* remove gap */

.error-item {
  background-color: #fff5f5; /* subtle red highlight */
}
</style>
